import { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { MessageSquareOff, Send } from 'lucide-react';
import { useForm } from 'react-hook-form';
import z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useDidMount, useWillUnmount } from 'rooks';

import HeaderWithBack from '@/components/common/HeaderWithBack';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Page from '@/components/layout/Page';
import { Card } from '@/components/ui/card';
import Error from '@/components/common/Error';
import PageLoading from '@/components/layout/PageLoading';
import {
  Empty,
  EmptyIcon,
  EmptyTitle,
  EmptyDescription
} from '@/components/ui/empty';
import { Form } from '@/components/ui/form';

import ConversationHeader from './components/view/ConversationHeader';
import Message from './components/view/Message';

import {
  useConversation,
  usePostConversationMessage,
  usePutReadConversation
} from '@/data/conversation';
import { useSelf } from '@/data/user';

const formSchema = z.object({
  message: z.string().min(1, { message: 'Message is required' })
});

const ChatConversation = () => {
  const params = useParams();
  const conversation = useConversation(params.id);
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const postMessage = usePostConversationMessage();
  const putReadConversation = usePutReadConversation();
  const messagesEndRef = useRef(null);
  const self = useSelf();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      message: ''
    }
  });

  useDidMount(() => {
    putReadConversation(params.id);
  });

  useWillUnmount(() => {
    putReadConversation(params.id);
  });

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(scrollToBottom, [conversation.data?.messages]);

  const onSubmit = async (values) => {
    setSubmitting(true);
    try {
      setApiError('');
      const updatedConversation = await postMessage(params.id, {
        content: values.message
      });
      await conversation.mutate(updatedConversation, false);
      form.reset();
    } catch (error) {
      setApiError(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (
    (conversation.isLoading && !conversation.data) ||
    (self.isLoading && !self.data)
  ) {
    return (
      <Page>
        <HeaderWithBack to="/conversation" title="Conversations" />
        <div className="flex justify-center items-center h-[calc(100vh-180px)]">
          <PageLoading />
        </div>
      </Page>
    );
  }

  return (
    <Page>
      <ConversationHeader conversation={conversation.data} />
      {apiError && <Error error={apiError} />}
      <div className="h-[74px]"></div>
      <div className="flex flex-col gap-2 justify-between conversation-container pb-4">
        <Card>
          <div className="flex-grow overflow-y-auto p-4 conversation-messages">
            {conversation.data.messages.length === 0 && (
              <Empty>
                <EmptyIcon>
                  <MessageSquareOff />
                </EmptyIcon>
                <EmptyTitle>No Messages</EmptyTitle>
                <EmptyDescription>Send the first Message!</EmptyDescription>
              </Empty>
            )}
            {conversation.data.messages.length > 0 &&
              conversation.data.messages.map((message) => (
                <Message key={message.id} message={message} />
              ))}
            <div ref={messagesEndRef} />
          </div>
        </Card>
        <Card className="flex flex-col bg-background">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="p-4 flex">
              <Input
                {...form.register('message')}
                placeholder="Type a message..."
                className="flex-grow mr-2"
              />
              <Button disabled={submitting}>
                <Send className="h-4 w-4 mr-2" />
                Send
              </Button>
            </form>
          </Form>
        </Card>
      </div>
    </Page>
  );
};

export default ChatConversation;
